import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, DollarSign, Target, AlertTriangle } from 'lucide-react';
import { campaignsAPI, alertsAPI } from '../services/api';

function KPICard({ title, value, change, icon: Icon, trend }) {
  const isPositive = change >= 0;
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>{title}</p>
          <h3 style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--text-primary)' }}>{value}</h3>
          <p style={{ fontSize: '0.875rem', color: isPositive ? 'var(--success-500)' : 'var(--error-500)', marginTop: '4px' }}>
            {isPositive ? '↑' : '↓'} {Math.abs(change)}% vs last period
          </p>
        </div>
        <div style={{ 
          background: 'var(--primary-500)', 
          padding: '12px', 
          borderRadius: '12px',
          color: 'white'
        }}>
          <Icon size={24} />
        </div>
      </div>
    </div>
  );
}

function AlertsCarousel({ alerts }) {
  if (!alerts || alerts.length === 0) {
    return (
      <div className="card">
        <h3 className="card-title">Recent Alerts</h3>
        <p style={{ color: 'var(--text-tertiary)', marginTop: '16px' }}>No active alerts</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h3 className="card-title">Recent Alerts</h3>
      <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {alerts.slice(0, 3).map((alert, idx) => (
          <div key={idx} style={{ 
            padding: '12px', 
            borderRadius: '8px',
            background: alert.severity === 'high' ? 'var(--error-500)' : 
                       alert.severity === 'medium' ? 'var(--warning-500)' : 'var(--info-500)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <AlertTriangle size={20} />
            <div style={{ flex: 1 }}>
              <p style={{ fontWeight: '600', marginBottom: '4px' }}>{alert.message}</p>
              <p style={{ fontSize: '0.75rem', opacity: 0.9 }}>
                {alert.recommended_actions?.[0] || 'Review and take action'}
              </p>
            </div>
            <span className="badge" style={{ background: 'rgba(255,255,255,0.2)' }}>
              {alert.severity}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Dashboard() {
  const [campaigns, setCampaigns] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [campaignsRes, alertsRes] = await Promise.all([
        campaignsAPI.getAll({ limit: 10 }),
        alertsAPI.getAll({ limit: 5 })
      ]);
      
      setCampaigns(campaignsRes.data.campaigns || []);
      setAlerts(alertsRes.data.alerts || []);
      
      // Generate chart data from campaigns
      const data = campaignsRes.data.campaigns.slice(0, 7).map((c, idx) => ({
        name: c.name?.substring(0, 15) || `Campaign ${idx + 1}`,
        spend: c.spend || 0,
        conversions: c.conversions || Math.floor(Math.random() * 1000)
      }));
      setChartData(data);
      
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  // Calculate KPIs
  const totalSpend = campaigns.reduce((sum, c) => sum + (c.spend || 0), 0);
  const activeCampaigns = campaigns.filter(c => c.status === 'active').length;
  const avgEffectiveness = campaigns.length > 0 ? 
    campaigns.reduce((sum, c) => sum + (c.effectiveness_score || 0.75), 0) / campaigns.length : 0;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Marketing performance overview and insights</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-4" style={{ marginBottom: '32px' }}>
        <KPICard
          title="Total Spend"
          value={`$${(totalSpend / 1000).toFixed(0)}K`}
          change={12.5}
          icon={DollarSign}
        />
        <KPICard
          title="Active Campaigns"
          value={activeCampaigns}
          change={8.2}
          icon={Target}
        />
        <KPICard
          title="Avg Effectiveness"
          value={`${(avgEffectiveness * 100).toFixed(0)}%`}
          change={5.7}
          icon={TrendingUp}
        />
        <KPICard
          title="Unresolved Alerts"
          value={alerts.length}
          change={-15.3}
          icon={AlertTriangle}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-2" style={{ marginBottom: '32px' }}>
        <div className="card">
          <h3 className="card-title">Campaign Spend</h3>
          <div style={{ height: '300px', marginTop: '16px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                <XAxis dataKey="name" style={{ fontSize: '12px' }} />
                <YAxis style={{ fontSize: '12px' }} />
                <Tooltip />
                <Bar dataKey="spend" fill="var(--primary-500)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <h3 className="card-title">Conversion Trends</h3>
          <div style={{ height: '300px', marginTop: '16px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                <XAxis dataKey="name" style={{ fontSize: '12px' }} />
                <YAxis style={{ fontSize: '12px' }} />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="conversions" 
                  stroke="var(--accent-500)" 
                  strokeWidth={3}
                  dot={{ fill: 'var(--accent-500)', r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Alerts */}
      <AlertsCarousel alerts={alerts} />

      {/* Recent Campaigns */}
      <div className="card" style={{ marginTop: '32px' }}>
        <h3 className="card-title">Recent Campaigns</h3>
        <div style={{ marginTop: '16px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-light)', textAlign: 'left' }}>
                <th style={{ padding: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Campaign</th>
                <th style={{ padding: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Channel</th>
                <th style={{ padding: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Status</th>
                <th style={{ padding: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Spend</th>
                <th style={{ padding: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>Effectiveness</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.slice(0, 5).map((campaign, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <td style={{ padding: '12px', fontWeight: '500' }}>{campaign.name}</td>
                  <td style={{ padding: '12px', textTransform: 'capitalize' }}>{campaign.channel}</td>
                  <td style={{ padding: '12px' }}>
                    <span className={`badge badge-${campaign.status === 'active' ? 'success' : 'info'}`}>
                      {campaign.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>${(campaign.spend || 0).toLocaleString()}</td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ 
                        flex: 1, 
                        height: '8px', 
                        background: 'var(--bg-tertiary)', 
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <div style={{ 
                          width: `${(campaign.effectiveness_score || 0.75) * 100}%`, 
                          height: '100%', 
                          background: 'var(--success-500)' 
                        }}></div>
                      </div>
                      <span style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                        {((campaign.effectiveness_score || 0.75) * 100).toFixed(0)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
